import { db } from "../firebase";
import {
  collection, doc, addDoc, setDoc, getDoc, getDocs, updateDoc, deleteDoc,
  query, orderBy, Timestamp, serverTimestamp, runTransaction,
} from "firebase/firestore";
import { gerarSlug } from "./slug";
import { visualizacao3dDoItem } from "./visualizacao3d";
import { fbCollectionPath } from "../hooks/useFirebase";

const DIA_MS = 24 * 60 * 60 * 1000;

// Domínio de produção — o link do orçamento precisa funcionar pro cliente
// mesmo quando gerado a partir do app local (Electron/Android) ou do
// ambiente de dev (localhost), então nunca usamos window.location.origin.
const SITE_URL = "https://printcontrol.netlify.app";

// ── Formato do orçamento ─────────────────────────────────────────
// Um orçamento é "carrinho": nomeCliente + lista de itens (cada item =
// um produto calculado). Nasceu como 1 produto só por orçamento — docs
// antigos não têm `itens`, então normalizamos na leitura pra sempre
// devolver o formato novo (sem precisar migrar nada no Firestore).
export function normalizarOrcamento(id, d) {
  if (Array.isArray(d.itens)) return { id, ...d };
  const {
    nome, fotos, altura, observacoes, preco, cores,
    custo_total, margem, horas_impressao, impressora_id,
    mao_obra_hrs, mao_obra_valor_hr, desconto_pct, desconto_fixo,
    unidades, filamentos_usados, materiais_usados, arquivo, visualizacao3d,
    ...resto
  } = d;
  return {
    id,
    ...resto,
    nomeCliente: d.nomeCliente || nome || "Orçamento sem nome",
    itens: [{
      nome: nome || "", fotos: fotos || [], altura: altura ?? null,
      observacoes: observacoes || null, preco: preco || 0, cores: cores || [],
      quantidade: 1, custo_total, margem, horas_impressao, impressora_id,
      mao_obra_hrs, mao_obra_valor_hr, desconto_pct, desconto_fixo,
      unidades, filamentos_usados, materiais_usados,
      ...(arquivo ? { arquivo } : {}), ...(visualizacao3d ? { visualizacao3d } : {}),
    }],
  };
}

// Orçamentos vivem sempre em users/{uid} (mesmo pra contas que ainda usam
// coleções raiz) — é uma feature nova, não precisa herdar a dívida técnica
// da migração em andamento (ver REESTRUTURACAO_PENDENCIAS.md).
export async function salvarOrcamento(uid, { nomeCliente, clienteId, item }) {
  const ref = await addDoc(collection(db, `users/${uid}/orcamentos`), {
    nomeCliente: (nomeCliente || "").trim() || "Cliente sem nome",
    clienteId: clienteId || null, // liga ao cadastro em Clientes (users/{uid}/clientes)
    itens: [item],
    link_gerado: false,
    created_at: serverTimestamp(),
    // TTL: configurar no console do Firebase uma política de expiração
    // no campo "expira_em" da coleção (grupo) "orcamentos".
    expira_em: Timestamp.fromMillis(Date.now() + 14 * DIA_MS),
  });
  return ref.id;
}

export async function getOrcamento(uid, id) {
  const snap = await getDoc(doc(db, `users/${uid}/orcamentos`, id));
  if (!snap.exists()) return null;
  return normalizarOrcamento(snap.id, snap.data());
}

export async function listarOrcamentos(uid) {
  let docs;
  try {
    const snap = await getDocs(query(collection(db, `users/${uid}/orcamentos`), orderBy("created_at", "desc")));
    docs = snap.docs;
  } catch {
    const snap = await getDocs(collection(db, `users/${uid}/orcamentos`));
    docs = snap.docs;
  }
  return docs.map(d => normalizarOrcamento(d.id, d.data()));
}

// Acrescenta mais um item ao carrinho — é assim que "Adicionar ao
// Orçamento" empilha vários produtos no mesmo orçamento/link.
export async function adicionarItemOrcamento(uid, orcamentoId, item) {
  const ref = doc(db, `users/${uid}/orcamentos`, orcamentoId);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error("Orçamento não encontrado (pode ter sido excluído)");
    const atual = normalizarOrcamento(orcamentoId, snap.data());
    const patch = {
      itens: [...atual.itens.map(it => Object.fromEntries(Object.entries(it).filter(([, valor]) => valor !== undefined))), item],
      expira_em: Timestamp.fromMillis(Date.now() + 14 * DIA_MS),
      link_desatualizado: !!atual.link_gerado,
    };
    transaction.update(ref, patch);
    return { ...atual, ...patch };
  });
}

// Edita um item específico (por índice) dentro do orçamento.
export async function atualizarItemOrcamento(uid, orcamentoId, index, dadosItem) {
  const ref = doc(db, `users/${uid}/orcamentos`, orcamentoId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Orçamento não encontrado");
  const itens = [...(snap.data().itens || [])];
  if (!itens[index]) throw new Error("Item não encontrado");
  itens[index] = { ...itens[index], ...dadosItem };
  await updateDoc(ref, {
    itens,
    expira_em: Timestamp.fromMillis(Date.now() + 14 * DIA_MS),
  });
}

// Aplica todos os preços juntos para que o total nunca fique parcialmente salvo.
export async function atualizarPrecosOrcamento(uid, orcamentoId, itensEsperados, precos) {
  if (!itensEsperados?.length || precos.length !== itensEsperados.length || precos.some(p => !Number.isFinite(p) || p < 0 || !Number.isSafeInteger(Math.round(p * 100)))) throw new Error("Revise os preços do orçamento.");
  const ref = doc(db, `users/${uid}/orcamentos`, orcamentoId);
  return runTransaction(db, async transaction => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) throw new Error("Orçamento não encontrado.");
    const atual = normalizarOrcamento(orcamentoId, snap.data());
    const assinatura = it => JSON.stringify([it.nome, it.produto_id || null, Number(it.quantidade ?? 1), Number(it.preco ?? 0), Number(it.custo_total ?? 0)]);
    if (atual.itens.length !== itensEsperados.length || atual.itens.some((it, i) => assinatura(it) !== assinatura(itensEsperados[i]))) throw new Error("O orçamento mudou. Reabra-o e confira os preços antes de aplicar.");
    const itens = atual.itens.map((it, i) => ({...Object.fromEntries(Object.entries(it).filter(([,v]) => v !== undefined)), preco:precos[i]}));
    const patch = { itens, expira_em:Timestamp.fromMillis(Date.now() + 14 * DIA_MS), link_desatualizado:!!atual.link_gerado };
    transaction.update(ref, patch);
    return {...atual, ...patch};
  });
}

// Remove um item do carrinho. Se era o último, apaga o orçamento inteiro
// (não faz sentido um orçamento vazio ficar pendurado).
export async function removerItemOrcamento(uid, orcamento, index) {
  const itensRestantes = (orcamento.itens || []).filter((_, i) => i !== index);
  if (itensRestantes.length === 0) {
    await apagarOrcamento(uid, orcamento);
    return;
  }
  await updateDoc(doc(db, `users/${uid}/orcamentos`, orcamento.id), { itens: itensRestantes });
}

export async function atualizarNomeClienteOrcamento(uid, orcamentoId, nomeCliente, clienteId) {
  await updateDoc(doc(db, `users/${uid}/orcamentos`, orcamentoId), {
    nomeCliente: (nomeCliente || "").trim() || "Cliente sem nome",
    clienteId: clienteId || null,
  });
}

export async function apagarOrcamento(uid, orcamento) {
  await deleteDoc(doc(db, `users/${uid}/orcamentos`, orcamento.id));
  try { await deleteDoc(doc(db, `users/${uid}/orcamentos_publicos`, orcamento.id)); } catch {}
  if (orcamento.slug) {
    try { await deleteDoc(doc(db, "orcamento_slugs", orcamento.slug)); } catch {}
  }
}

// Slug curto e global (sem uid na URL) — pra achar de qual vendedor é
// um slug, precisa desse mapeamento à parte (mesma ideia de lojas/{slug}).
// Gerado UMA vez (na primeira vez que o link é criado) e nunca muda depois
// — editar o orçamento e "renovar link" continuam batendo no mesmo slug,
// então o link mandado pro cliente nunca precisa trocar.
async function gerarSlugUnico(nomeBase) {
  const base = gerarSlug(nomeBase) || "orcamento";
  let slug = base, n = 2;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const snap = await getDoc(doc(db, "orcamento_slugs", slug));
    if (!snap.exists()) return slug;
    slug = `${base}-${n++}`;
  }
}

// Snapshot público (só os dados que o cliente final pode ver — nunca custo,
// margem, peso ou IDs de estoque). Reusa o mesmo id do orçamento privado e o
// mesmo slug pra sempre, então "gerar link" de novo simplesmente renova os
// 3 dias sem trocar a URL.
export async function gerarLinkOrcamento(uid, orcamento) {
  let slug = orcamento.slug;
  if (!slug) {
    slug = await gerarSlugUnico(orcamento.nomeCliente || "orcamento");
    await setDoc(doc(db, "orcamento_slugs", slug), { uid, orcamentoId: orcamento.id, criado_em: serverTimestamp() });
    await updateDoc(doc(db, `users/${uid}/orcamentos`, orcamento.id), { slug });
  }

  const paraPublico = it => ({
    nome: it.nome || "", fotos: it.fotos || [], preco: it.preco || 0,
    quantidade: it.quantidade || 1, altura: it.altura ?? null,
    observacoes: it.observacoes || "", cores: it.cores || [],
    visualizacao3d: visualizacao3dDoItem(it),
  });
  const produtos = new Map();
  const itens = await Promise.all((orcamento.itens || []).map(async it => {
    const publico = paraPublico(it);
    // Itens antigos do catálogo ainda não copiavam o arquivo para o orçamento.
    // A seleção já salva no próprio item permanece soberana.
    if (publico.visualizacao3d || Object.prototype.hasOwnProperty.call(it, "visualizacao3d") || it.arquivo || !it.produto_id) return publico;
    try {
      if (!produtos.has(it.produto_id)) produtos.set(it.produto_id, getDoc(doc(db, fbCollectionPath("produtos"), it.produto_id)));
      const produto = await produtos.get(it.produto_id);
      if (produto.exists()) publico.visualizacao3d = visualizacao3dDoItem(produto.data());
    } catch { /* produto excluído ou indisponível não impede gerar o orçamento */ }
    return publico;
  }));
  const pubRef = doc(db, `users/${uid}/orcamentos_publicos`, orcamento.id);
  await runTransaction(db, async transaction => {
    const anteriorSnap = await transaction.get(pubRef);
    const anterior = anteriorSnap.exists() ? anteriorSnap.data() : null;
    // Atualizar o carrinho preserva aprovações e pedidos dos itens que não
    // mudaram. Os itens novos ou alterados precisam de uma nova aprovação.
    const anteriores = (anterior?.itens || []).map(paraPublico);
    const usados = new Set();
    const aprovados = {}, pedidosRegistrados = {};
    itens.forEach((item, i) => {
      if (orcamento.itens[i]?.pedido_id) pedidosRegistrados[i] = true;
      const j = anteriores.findIndex((antes, index) => !usados.has(index) && JSON.stringify(antes) === JSON.stringify(item));
      if (j < 0) return;
      usados.add(j);
      if (anterior.aprovados?.[j] || anterior.aprovado) aprovados[i] = true;
      if (anterior.pedidosRegistrados?.[j]) pedidosRegistrados[i] = true;
    });
    const total = itens.reduce((s, it) => s + (parseFloat(it.preco) || 0) * (it.quantidade || 1), 0);

    const publico = {
      nomeCliente: orcamento.nomeCliente || "",
      itens, total,
      // Mapa índice-do-item -> aprovado (bool) — cliente aprova cada produto
      // separadamente, não o orçamento inteiro de uma vez.
      aprovados, pedidosRegistrados,
      aprovado_em: itens.length > 0 && Object.keys(aprovados).length === itens.length ? anterior?.aprovado_em || null : null,
      primeira_visualizacao_em: anterior?.primeira_visualizacao_em || null,
      ultima_visualizacao_em: anterior?.ultima_visualizacao_em || null,
      criado_em: serverTimestamp(),
      // TTL: configurar no console do Firebase uma política de expiração
      // no campo "expira_em" da coleção (grupo) "orcamentos_publicos".
      expira_em: Timestamp.fromMillis(Date.now() + 3 * DIA_MS),
    };
    transaction.set(pubRef, publico);
    transaction.update(doc(db, `users/${uid}/orcamentos`, orcamento.id), { link_gerado: true, link_desatualizado: false, link_gerado_em: serverTimestamp() });
  });
  return `${SITE_URL}/orcamento/${slug}`;
}

// Não gera nada novo — só monta a URL a partir do slug já salvo no
// orçamento (usado por "copiar link"/"abrir", que não devem renovar os 3 dias).
export function linkOrcamento(orcamento) {
  return orcamento?.slug ? `${SITE_URL}/orcamento/${orcamento.slug}` : null;
}

// Link "antigo" (uid+id), mantido só pra links já mandados antes desta
// mudança continuarem funcionando — nunca gerado por código novo.
export function linkOrcamentoLegado(uid, id) {
  return `${SITE_URL}/orcamento/${uid}/${id}`;
}
